import { useState, useEffect } from 'react';
import { templateService } from '../../services/templateService';
import type { SharePointFile } from '@shared/types';
import { DocumentIcon, ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface SharePointFilePickerProps {
  selectedFileId?: string;
  onSelect: (file: SharePointFile | null) => void;
}

export function SharePointFilePicker({ selectedFileId, onSelect }: SharePointFilePickerProps) {
  const [files, setFiles] = useState<SharePointFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const templateFiles = await templateService.listSharePointTemplateFiles();
      setFiles(templateFiles);
      
      if (templateFiles.length === 0) {
        setError('No template files found in SharePoint Templates folder. Upload .docx, .dotx, or .pdf files.');
      }
    } catch (err) {
      console.error('Failed to load SharePoint files:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load template files';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const selectedFile = files.find(f => f.id === selectedFileId);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          SharePoint Template File
        </label>
        <button
          type="button"
          onClick={loadFiles}
          disabled={loading}
          className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-gray-500">
          <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />
          Loading template files...
        </div>
      ) : error ? (
        <div className="flex items-start gap-2 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-yellow-800">{error}</p>
            {error.includes('SharePoint not connected') && (
              <p className="text-xs text-yellow-700 mt-1">
                Go to Settings → Connections to connect SharePoint.
              </p>
            )}
            {error.includes('No template files') && (
              <p className="text-xs text-yellow-700 mt-1">
                Upload .docx (contracts) or .pdf (ACORD blanks) to the Templates folder in SharePoint.
              </p>
            )}
          </div>
        </div>
      ) : files.length > 0 ? (
        <>
          <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-200">
            {files.map((file) => (
              <label
                key={file.id}
                className={`flex items-start gap-3 p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                  selectedFileId === file.id ? 'bg-blue-50 hover:bg-blue-50' : ''
                }`}
              >
                <input
                  type="radio"
                  name="sharepoint-file"
                  checked={selectedFileId === file.id}
                  onChange={() => onSelect(file)}
                  className="mt-1 w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <DocumentIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {file.name}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                    <span>{(file.size / 1024).toFixed(1)} KB</span>
                    <span>•</span>
                    <span>Modified {new Date(file.lastModified).toLocaleDateString()}</span>
                  </div>
                </div>
              </label>
            ))}
          </div>

          {selectedFile && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-blue-900">Selected:</p>
                  <p className="text-sm text-blue-800 truncate">{selectedFile.name}</p>
                </div>
                <button
                  type="button"
                  onClick={() => onSelect(null)}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Clear
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <DocumentIcon className="w-12 h-12 mx-auto text-gray-400 mb-2" />
          <p className="text-sm">No template files found</p>
          <p className="text-xs text-gray-400 mt-1">
            Upload .docx, .dotx, or .pdf files to the SharePoint Templates folder
          </p>
        </div>
      )}

      <p className="text-xs text-gray-500">
        .docx / .dotx (contract templates) and .pdf (ACORD blanks) from the Templates folder are shown.
      </p>
    </div>
  );
}

